import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { containsProfanity } from '../utils/profanityFilter';
import MINECRAFT_BLOCKS from '../data/minecraftBlocks';
import { spriteMap, SPRITE_SIZE } from '../data/itemSpriteMap';
import { renderMinecraftTextHTML, stripMinecraftCodes } from '../utils/minecraftIcons';
import useTurnstileToken from '../utils/useTurnstileToken';
import VerifyingProgress from '../components/VerifyingProgress';
import EventAuthBar from '../components/EventAuthBar';
import { signalTransitionReady } from '../components/EventTransition';
import { useAppAuth } from '../context/AuthContext';
import './LiveEventPage.css';
import './BlockDeletePage.css';

const PARTYKIT_HOST = process.env.REACT_APP_PARTYKIT_HOST || 'thetopia-live.lafless.partykit.dev';
const DISPLAY_NAME_KEY = 'thetopia_event_displayname';

// Preload sprite sheet
new Image().src = '/img/item-sprites.png';

/* ── Fingerprint (lazy-loaded) ── */
let fpPromise = null;
async function getFingerprint() {
  if (!fpPromise) {
    fpPromise = import('@fingerprintjs/fingerprintjs').then(async (FP) => {
      const agent = await FP.load();
      const result = await agent.get();
      return result.visitorId;
    });
  }
  return fpPromise;
}

/* ── Block Sprite ── */
// Blocks without sprite sheet entries — map to their item icon
const BLOCK_ICON_FALLBACKS = {
  water: 'water_bucket',
  lava: 'lava_bucket',
  powder_snow: 'powder_snow_bucket',
  chain: 'iron_chain',
  skeleton_head: 'skeleton_skull',
};

function BlockIcon({ block, size = 32 }) {
  // Try sprite sheet first
  const spriteKey = spriteMap[block] ? block : BLOCK_ICON_FALLBACKS[block];
  const pos = spriteKey && spriteMap[spriteKey];
  if (pos) {
    const scale = size / SPRITE_SIZE;
    return (
      <div
        className="bd-block-icon"
        style={{
          width: size,
          height: size,
          backgroundImage: 'url(/img/item-sprites.png)',
          backgroundPosition: `-${pos[0] * scale}px -${pos[1] * scale}px`,
          backgroundSize: `${1248 * scale}px ${1248 * scale}px`,
          imageRendering: 'pixelated',
        }}
      />
    );
  }
  // Fallback to individual PNG in /img/items/
  return (
    <div
      className="bd-block-icon"
      style={{
        width: size,
        height: size,
        backgroundImage: `url(/img/items/${block}.png)`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        imageRendering: 'pixelated',
      }}
    />
  );
}

/* ── Username Gate ── */
function UsernameGate({ onSubmit }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 24) {
      setError('Name must be 2-24 characters');
      return;
    }
    if (containsProfanity(trimmed)) {
      setError('Please choose an appropriate name');
      return;
    }
    localStorage.setItem(DISPLAY_NAME_KEY, trimmed);
    onSubmit(trimmed);
  };

  return (
    <div className="le-gate">
      <div className="le-gate-card">
        <h2 className="le-gate-title">Enter Display Name</h2>
        <p className="le-gate-desc">This is how other viewers will see you</p>
        <form onSubmit={handleSubmit} className="le-gate-form">
          <input
            type="text"
            className="le-gate-input"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            placeholder="Your name..."
            maxLength={24}
            autoFocus
          />
          {error && <span className="le-gate-error">{error}</span>}
          <button type="submit" className="le-gate-btn">Join Event</button>
        </form>
      </div>
    </div>
  );
}

/* ── Round Header ── */
function RoundHeader({ state }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (state.roundState === 'active' && state.roundStartedAt) {
      const tick = () => {
        const elapsed = Date.now() - state.roundStartedAt - (state.pausedElapsed || 0);
        const remaining = Math.max(0, (state.roundDurationSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      };
      tick();
      const interval = setInterval(tick, 250);
      return () => clearInterval(interval);
    }

    if (state.roundState === 'intermission' && state.intermissionStartedAt) {
      const tick = () => {
        const elapsed = Date.now() - state.intermissionStartedAt - (state.pausedElapsed || 0);
        const remaining = Math.max(0, (state.intermissionSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      };
      tick();
      const interval = setInterval(tick, 250);
      return () => clearInterval(interval);
    }

    if (state.roundState === 'paused' && state.pausedAt) {
      if (state.prePauseState === 'intermission' && state.intermissionStartedAt) {
        const elapsed = state.pausedAt - state.intermissionStartedAt - (state.pausedElapsed || 0);
        const remaining = Math.max(0, (state.intermissionSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      } else if (state.roundStartedAt) {
        const elapsed = state.pausedAt - state.roundStartedAt - (state.pausedElapsed || 0);
        const remaining = Math.max(0, (state.roundDurationSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      }
    } else if (state.roundState === 'idle') {
      setTimeLeft(null);
    }
  }, [state.roundStartedAt, state.roundDurationSec, state.pausedElapsed, state.roundState, state.pausedAt, state.intermissionStartedAt, state.intermissionSec, state.prePauseState]);

  const formatTime = (seconds) => {
    if (seconds == null) return '-';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isPaused = state.roundState === 'paused';

  const stateInfo = {
    active: {
      label: 'LIVE',
      className: 'le-badge-live',
      hint: 'Vote for a block below! The most voted block will be removed in a large radius around all players.',
    },
    intermission: {
      label: 'INTERMISSION',
      className: 'le-badge-intermission',
      hint: 'The winning block is being deleted in-game. Next round starts soon so plan your next vote!',
    },
    paused: {
      label: 'PAUSED',
      className: 'le-badge-paused',
      hint: 'The event has been paused. Hang tight!',
    },
    idle: {
      label: 'WAITING',
      className: 'le-badge-idle',
      hint: 'The event hasn\'t started yet. Stay on this page, you\'ll be able to vote once it begins!',
    },
  }[state.roundState] || { label: state.roundState?.toUpperCase(), className: '', hint: '' };

  return (
    <>
      <div className="le-round-header">
        <div className="le-round-info">
          <span className={`le-state-badge ${stateInfo.className}`}>{stateInfo.label}</span>
          {state.roundNumber > 0 && (
            <span className="le-round-number">Round {state.roundNumber}</span>
          )}
        </div>
        <div className={`le-timer${isPaused ? ' le-timer-paused' : ''}`}>{formatTime(timeLeft)}</div>
        <div className="le-connections">
          <span className="le-connections-dot" />
          {state.connections} watching
        </div>
      </div>
      {stateInfo.hint && (
        <div className={`le-status-hint le-status-hint-${state.roundState}`}>
          {stateInfo.hint}
        </div>
      )}
    </>
  );
}

/* ── Minecraft Tooltip ── */
function McTooltip({ text, x, y }) {
  if (!text) return null;
  return (
    <div
      className="mc-tooltip"
      style={{ left: x, top: y }}
    >
      <div className="mc-tooltip-outer">
        <div className="mc-tooltip-inner">
          <span className="mc-tooltip-text">{text}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Block Grid ── */
const BlockGrid = React.memo(function BlockGrid({ blockVotes, yourVote, blockedBlocks, deletedBlocks, onVote, disabled }) {
  const [search, setSearch] = useState('');
  const [tooltip, setTooltip] = useState({ text: null, x: 0, y: 0 });
  const gridRef = useRef(null);
  const blockedSet = useMemo(() => new Set(blockedBlocks || []), [blockedBlocks]);
  const deletedSet = useMemo(() => new Set(deletedBlocks || []), [deletedBlocks]);

  const filteredBlocks = useMemo(() => {
    const term = search.toLowerCase().replace(/\s+/g, '_');
    const filtered = MINECRAFT_BLOCKS.filter(b => {
      if (blockedSet.has(b.value) || deletedSet.has(b.value)) return false;
      if (!term) return true;
      return b.value.includes(term) || b.label.toLowerCase().includes(search.toLowerCase());
    });

    // When not searching, sort blocks with votes to the front
    if (!term && blockVotes) {
      filtered.sort((a, b) => {
        const va = blockVotes[a.value] || 0;
        const vb = blockVotes[b.value] || 0;
        return vb - va;
      });
    }

    return filtered;
  }, [search, blockedSet, blockVotes]);

  // Find leading block
  const leadingBlock = useMemo(() => {
    if (!blockVotes) return null;
    let max = 0;
    let leader = null;
    for (const [id, count] of Object.entries(blockVotes)) {
      if (count > max) { max = count; leader = id; }
    }
    return leader;
  }, [blockVotes]);


  const handleBlockMouse = useCallback((e, label) => {
    if (e.type === 'mouseleave') {
      setTooltip({ text: null, x: 0, y: 0 });
    } else {
      setTooltip({ text: label, x: e.clientX, y: e.clientY });
    }
  }, []);

  return (
    <div className="bd-grid-section">
      <div className="bd-search-wrap">
        <input
          type="text"
          className="bd-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search blocks..."
          autoComplete="off"
          spellCheck={false}
        />
        <span className="bd-search-count">{filteredBlocks.length} blocks</span>
      </div>
      <div className="bd-grid" ref={gridRef}>
        {filteredBlocks.map((block) => {
          const votes = blockVotes?.[block.value] || 0;
          const isYours = block.value === yourVote;
          const isLeading = block.value === leadingBlock && votes > 0;

          return (
            <button
              key={block.value}
              className={`bd-block ${isYours ? 'bd-block-voted' : ''} ${isLeading ? 'bd-block-leading' : ''}`}
              onClick={() => onVote(block.value)}
              disabled={disabled}
              onMouseEnter={(e) => handleBlockMouse(e, block.label)}
              onMouseMove={(e) => handleBlockMouse(e, block.label)}
              onMouseLeave={(e) => handleBlockMouse(e, null)}
            >
              <BlockIcon block={block.value} size={32} />
              {votes > 0 && (
                <span className={`bd-block-votes ${isLeading ? 'bd-block-votes-leading' : ''}`}>
                  {votes}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {filteredBlocks.length === 0 && (
        <div className="bd-grid-empty">No blocks match your search</div>
      )}
      <McTooltip text={tooltip.text} x={tooltip.x} y={tooltip.y} />
    </div>
  );
});

/* ── Leaderboard ── */
const Leaderboard = React.memo(function Leaderboard({ blockVotes, yourVote }) {
  const sorted = useMemo(() => {
    if (!blockVotes) return [];
    return Object.entries(blockVotes)
      .filter(([, v]) => v > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);
  }, [blockVotes]);

  if (sorted.length === 0) return null;

  return (
    <div className="bd-leaderboard">
      <h3 className="bd-leaderboard-title">Top Votes</h3>
      <div className="bd-leaderboard-list">
        {sorted.map(([blockId, votes], i) => {
          const block = MINECRAFT_BLOCKS.find(b => b.value === blockId);
          const isYours = blockId === yourVote;
          return (
            <div key={blockId} className={`bd-leader-item ${i === 0 ? 'bd-leader-first' : ''} ${isYours ? 'bd-leader-yours' : ''}`}>
              <span className="bd-leader-rank">{i + 1}</span>
              <BlockIcon block={blockId} size={24} />
              <span className="bd-leader-name">{block?.label || blockId.replace(/_/g, ' ')}</span>
              <span className="bd-leader-votes">{votes}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
});

/* ── Round History ── */
const RoundHistory = React.memo(function RoundHistory({ history }) {
  if (!history || history.length === 0) return null;

  return (
    <div className="bd-history">
      <h3 className="bd-history-title">Deleted Blocks</h3>
      <div className="bd-history-list">
        {[...history].reverse().map((entry) => {
          const block = MINECRAFT_BLOCKS.find(b => b.value === entry.winner.blockId);
          return (
            <div key={entry.roundNumber} className="bd-history-item">
              <span className="bd-history-round">R{entry.roundNumber}</span>
              <BlockIcon block={entry.winner.blockId} size={20} />
              <span className="bd-history-block">{block?.label || entry.winner.blockId.replace(/_/g, ' ')}</span>
              <span className="bd-history-meta">
                {entry.winner.votes} vote{entry.winner.votes !== 1 ? 's' : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
});

/* ── Main Block Delete Page ── */
export default function BlockDeletePage({ event }) {
  const slug = event?.slug;
  const { isSignedIn, minecraftUsername } = useAppAuth();
  const { token: sessionToken, error: turnstileError, retry: retryTurnstile } = useTurnstileToken();
  const [displayName, setDisplayName] = useState(() => localStorage.getItem(DISPLAY_NAME_KEY) || '');

  // Auto-set display name from Minecraft account when signed in
  useEffect(() => {
    if (isSignedIn && minecraftUsername && displayName !== minecraftUsername) {
      localStorage.setItem(DISPLAY_NAME_KEY, minecraftUsername);
      setDisplayName(minecraftUsername);
    }
  }, [isSignedIn, minecraftUsername, displayName]);
  const [fingerprint, setFingerprint] = useState(null);
  const [roomState, setRoomState] = useState(null);
  const [wsError, setWsError] = useState(null);
  const [lastError, setLastError] = useState(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => {
    if (event?.name) {
      document.title = `${stripMinecraftCodes(event.name)} - TheTopia (Minecraft Events)`;
    }
  }, [event?.name]);

  // Signal transition ready if showing username gate (no WS to wait for)
  useEffect(() => {
    if (!displayName) signalTransitionReady();
  }, [displayName]);

  // Load fingerprint
  useEffect(() => {
    getFingerprint().then(setFingerprint).catch(() => {
      setFingerprint(`anon-${Math.random().toString(36).slice(2, 10)}`);
    });
  }, []);

  // WebSocket connection
  const connect = useCallback(() => {
    if (!displayName || !fingerprint || !slug || !sessionToken) return;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${protocol}://${PARTYKIT_HOST}/parties/block/${slug}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsError(null);
      signalTransitionReady();
      ws.send(JSON.stringify({
        type: 'join',
        displayName,
        fingerprint,
        token: sessionToken,
      }));
    };


    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'state') {
          setRoomState(msg);
        } else if (msg.type === 'update') {
          setRoomState(prev => {
            if (!prev) return null;
            let blockVotes;
            if (msg.delta) {
              // New server: merge delta into existing
              blockVotes = { ...prev.blockVotes };
              if (msg.blockVotes) {
                for (const [blockId, count] of Object.entries(msg.blockVotes)) {
                  if (count === 0) delete blockVotes[blockId];
                  else blockVotes[blockId] = count;
                }
              }
            } else {
              // Old server fallback: full replace
              blockVotes = msg.blockVotes ?? prev.blockVotes;
            }
            return {
              ...prev,
              blockVotes,
              ...(msg.connections !== undefined && { connections: msg.connections }),
              ...(msg.yourVote !== undefined && { yourVote: msg.yourVote }),
            };
          });
        } else if (msg.type === 'vote-ack') {
          setRoomState(prev => prev ? { ...prev, yourVote: msg.yourVote } : null);
        } else if (msg.type === 'viewers') {
          setRoomState(prev => prev ? { ...prev, connections: msg.connections } : null);
        } else if (msg.type === 'error') {
          setLastError(msg.message);
          setTimeout(() => setLastError(null), 4000);
        }
      } catch {}
    };

    ws.onclose = () => {
      wsRef.current = null;
      reconnectRef.current = setTimeout(() => connect(), 2000);
    };

    ws.onerror = () => {
      setWsError('Connection lost. Reconnecting...');
    };
  }, [displayName, fingerprint, slug, sessionToken]);

  useEffect(() => {
    if (displayName && fingerprint && sessionToken) {
      connect();
    }
    return () => {
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect, displayName, fingerprint]);

  const sendMessage = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const handleVote = useCallback((blockId) => {
    sendMessage({ type: 'vote-block', blockId, fingerprint });
  }, [sendMessage, fingerprint]);

  // Username gate
  if (!displayName) {
    return (
      <section className="le-page">
        <UsernameGate onSubmit={setDisplayName} />
      </section>
    );
  }

  const isActive = roomState?.roundState === 'active';

  return (
    <section className="le-page">
      <div className="bd-layout">
        <div className="bd-container">
          <h1 className="le-event-title" dangerouslySetInnerHTML={{ __html: renderMinecraftTextHTML(event?.name || 'Block Delete') }} />

          {wsError && <div className="le-ws-error">{wsError}</div>}
          {lastError && <div className="le-cmd-error">{lastError}</div>}

          {roomState ? (
            <>
              <RoundHeader state={roomState} />

              <div className="bd-main">
                <div className="bd-main-left">
                  <BlockGrid
                    blockVotes={roomState.blockVotes}
                    yourVote={roomState.yourVote}
                    blockedBlocks={roomState.blockedBlocks}
                    deletedBlocks={roomState.deletedBlocks}
                    onVote={handleVote}
                    disabled={!isActive}
                  />
                </div>
                <div className="bd-main-right">
                  <Leaderboard
                    blockVotes={roomState.blockVotes}
                    yourVote={roomState.yourVote}
                  />
                  <RoundHistory history={roomState.history} />
                </div>
              </div>
            </>
          ) : (
            <div className="le-connecting">
              {turnstileError ? (
                <>
                  <span>Verification failed</span>
                  <button className="le-retry-btn" onClick={retryTurnstile}>Retry</button>
                </>
              ) : (
                <VerifyingProgress
                  done={!!sessionToken && !!roomState}
                  label={!sessionToken ? 'Verifying...' : 'Connecting to live server...'}
                />
              )}
            </div>
          )}

          <div className="le-user-bar">
            <span className="le-user-name">Playing as: {displayName}</span>
            {!isSignedIn && (
              <button
                className="le-change-name"
                onClick={() => {
                  localStorage.removeItem(DISPLAY_NAME_KEY);
                  setDisplayName('');
                }}
              >
                Change Name
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
